import { Injectable } from '@angular/core';
import { AccountService, SchoolsService } from '@core/services';
import { HelpersBffService } from '@core/services/bff/helpers.service';
import { DestroyableComponent } from '@models/destroyable.component';
import { LibraryRouter } from '@models/library.router';
import { Collection, Content, CSchool, Meeting, User, UserService } from 'lingo2-models';
import { BehaviorSubject } from 'rxjs';

export type CardContentTypes = 'user-service' | 'collections' | 'lesson' | 'meeting' | 'tutors' | 'school';

export enum CardContentView {
  vertical = 'vertical',
  horizontal = 'horizontal',
  'vertical-square' = 'vertical-square',
  'wide-horizontal' = 'wide-horizontal',
  'wide-vertical' = 'wide-vertical',
}

export type ContentTypes = UserService | User | Meeting | CSchool | Collection | Content;

export interface DataForModal {
  openedData: ContentTypes;
  route: string;
}

export interface SendToModalData {
  data: DataForModal;
  type: CardContentTypes;
  state: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GridCardService extends DestroyableComponent {
  private modal = this.register(new BehaviorSubject<SendToModalData>(null));
  public modal$ = this.modal.asObservable();

  public modalData: SendToModalData = {
    data: null,
    type: null,
    state: null,
  };

  public tempData: DataForModal = {
    openedData: null,
    route: null,
  };

  public constructor(private helpers: HelpersBffService) {
    super();
  }

  public contentType(content: ContentTypes): CardContentTypes {
    if (content instanceof Meeting) {
      return 'meeting';
    } else if (content instanceof UserService) {
      return 'user-service';
    } else if (content instanceof Collection) {
      return 'collections';
    } else if (content instanceof User) {
      return 'tutors';
    } else if (content instanceof CSchool) {
      return 'school';
    } else {
      return 'lesson';
    }
  }

  public generateModalData(data: ContentTypes): SendToModalData {
    if (this.contentType(data) === 'user-service') {
      this.tempData.route = this.helpers.userServiceRouteUniversal(data as UserService);
    } else if (this.contentType(data) === 'collections') {
      this.tempData.route = LibraryRouter.collectionRouteUniversal(data as Collection);
    } else if (this.contentType(data) === 'tutors') {
      this.tempData.route = AccountService.accountRoute(data as User).join('/');
    } else if (this.contentType(data) === 'school') {
      this.tempData.route = SchoolsService.schoolRoute(data as CSchool, false).join('/');
    } else {
      this.tempData.route = LibraryRouter.contentRouteUniversal(data as Content);
    }

    this.tempData.openedData = data;
    return {
      data: this.tempData,
      type: this.contentType(data),
      state: true,
    };
  }

  public setModalData(data: ContentTypes) {
    this.modalData = this.generateModalData(data);
  }

  public resetModalData() {
    this.modalData = {
      data: {
        openedData: null,
        route: null,
      },
      type: null,
      state: false,
    };
  }

  public contentLikeMeeting(data: ContentTypes) {
    return data as Meeting;
  }

  public contentLikeUserService(data: ContentTypes) {
    return data as UserService;
  }

  public contentLikeCollection(data: ContentTypes) {
    return data as Collection;
  }

  public contentLikeUser(data: ContentTypes) {
    return data as User;
  }

  public contentLikeCSchool(data: ContentTypes) {
    return data as CSchool;
  }

  public contentLikeContent(data: ContentTypes) {
    return data as Content;
  }
}
